import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CalendarScheduleOrder } from "components/calendar-schedule-order/calendar-schedule-order";
import {
  LoadingController,
  ModalController,
  PopoverController,
} from "ionic-angular";
import CalendarEvent from "models/calendar/calendar-event";
import UnassignedOrder from "models/calendar/unassigned-order";
import CompanyRole from "models/company/company-role";
import { CompanyTeam } from "models/company/company-team";
import Order from "models/order";
import PatientContact from "models/patient-chart/contacts/patient-contact";
import { Patient } from "models/patient-chart/patient";
import { NavigationService } from "navigation/navigation.service";
import { CalendarService } from "providers/calendar-service/calendar-service";
import { CareTeamsService } from "providers/care-teams-service/care-teams-service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Observable } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import { QaQueueComments } from "../../../../components/qa-control/models/qa-queue-comments";
import HopeFlag from "../../../../models/hope/hope-type";

@Component({
  selector: "task-details-row",
  templateUrl: "task-details-row.html",
})
export class TaskDetailsRowComponent {
  @Input() task: CalendarEvent;
  @Input() commentCategories: any;
  @Input() visitsComments: QaQueueComments;

  @Output() eventUpdated = new EventEmitter();

  spinnerCounter = 0;
  spinner = null;
  private patientChartId: number;
  isTaskPastDue: boolean = false;
  showHopeDescription: boolean = false;

  private readonly CODE_INITIAL_ASSESSMENT = "CL003.1";
  private readonly VISIT_COMPLETED = "COMPLETED";
  private readonly HUV1 = "HUV#1";
  private readonly HUV2 = "HUV#2";
  private readonly SFV = "SFV";
  private readonly INITIAL_ASSESSMENT = "INV";

  constructor(
    private careTeamsService: CareTeamsService,
    private patientChartsService: ReferralsServiceProvider,
    private calendarService: CalendarService,
    private modalCtrl: ModalController,
    private navSvc: NavigationService,
    private loadingCtrl: LoadingController,
    private stateData: StateManagementService,
    public popover: PopoverController
  ) {}

  ngOnInit() {
    if (this.task.patientFirstName && this.task.patientLastName) {
      this.task.patientName =
        this.task.patientLastName + ", " + this.task.patientFirstName;
      if (this.task.patientMiddleInitial)
        this.task.patientName += " " + this.task.patientMiddleInitial;
    }
    this.checkIfTaskPastDue();
  }

  seeMore(task: CalendarEvent) {
    this.increaseSpinnerCount();
    let observables = [];
    // get the patient
    observables.push(
      this.patientChartsService.getPatientById(task.patientChartId, false, true)
    );
    // get the company teams
    observables.push(this.calendarService.getAllTeamsTyped());
    // get the company roles
    observables.push(this.calendarService.getTeamRoles());

    // if (task.orderId && task.orderItemId) {
    //     observables.push(this.ordersService.getOrderById(task.orderId).map(x => x).catch(x => Observable.of([])));
    // }

    forkJoin(observables)
      .switchMap((outputs) => {
        let patient = outputs[0] as Patient;
        let teams = outputs[1] as CompanyTeam[];
        let roles = outputs[2] as CompanyRole[];
        // get the caregivers using the patient's external ID -- build in a "catch" so that we handle the 404 because IDG will pop here if the team hasn't been setup
        return this.careTeamsService
          .getPatientCaregiversTyped(patient.externalId)
          .map((contacts) => ({ patient, contacts, teams, roles }))
          .catch((x) =>
            Observable.of({
              patient,
              contacts: [] as PatientContact[],
              teams,
              roles,
            })
          );
      })
      .subscribe(
        ({ patient, contacts, teams, roles }) => {
          let primary = new PatientContact();
          let order = null;

          if (contacts && contacts.length > 0) {
            let contact = contacts.filter((x: PatientContact) => {
              return x.primaryCaregiver === true;
            });

            if (contact && contact.length === 1) {
              primary = contact[0];
            }
          }

          this.decreaseSpinnerCount();
          this.taskViewModal(task, patient, primary, teams, roles, order);
        },
        (err) => {
          let msg = "Error loading up details for Task Edit";
          console.error(msg);
          console.error(err);
          this.decreaseSpinnerCount();
          this.calendarService.presentErrorToast(err, msg);
        }
      );
  }

  taskViewModal(
    calendarEvent: CalendarEvent,
    patient: Patient,
    patientContact: PatientContact,
    teams: CompanyTeam[],
    roles: CompanyRole[],
    order: Order
  ): void {
    let mode = "";
    const cssClass = mode == "map" ? "map-view-task" : "other-view-task";

    let eventViewModal = this.modalCtrl.create(
      "CalendarEventView",
      {
        calendarEvent: calendarEvent,
        patient: patient,
        primaryContact: patientContact,
        order: order,
        hideMoreButton: true,
      },
      { cssClass: cssClass }
    );

    eventViewModal.present();

    eventViewModal.onDidDismiss((data) => {
      if (!data) return;
      if (data && data.stopEvents) {
        console.log("Stop events requested");
      } else if (data.visitStarted) {
        // NOVA-1490 follow-up: Sync sessionStorage patient context before navigating.
        if (patient) {
          this.navSvc.setSessionStorage(patient.externalId, patient.id);
        }
        this.navSvc.goToInternalPage(`AssessmentHomePage`, {
          patChartId: data.visitData.externalPatientChartId,
          assessmentId: data.visitData.visitId,
        });
      } else if (data.visitVisit) {
        this.stateData.setAssessmentType(null);
        this.stateData.setSectionId(null);
        // NOVA-1490 follow-up: Sync sessionStorage patient context before navigating.
        if (patient) {
          this.navSvc.setSessionStorage(patient.externalId, patient.id);
        }
        this.navSvc.goToInternalPage("AssessmentHomePage", {
          patChartId: data.patientChartId,
          assessmentId: data.visitId,
        });
      } else if (data && data.patientCalendar) {
        this.patientChartId = data.patientChartId;
        this.goToPatientCalendar(data);
      } else if (data && data.action && data.action.editSeries) {
        this.editSeries(calendarEvent, data.patientChart, teams, roles);
      } else {
        this.editEventView(calendarEvent, data.patientChart, teams, roles);
      }
    });
  }

  private goToPatientCalendar(data) {
    window.sessionStorage.clear();
    this.navSvc.goToInternalPage("NewCalendarPage", {
      patChartId: data.patientChartId,
    });
  }

  increaseSpinnerCount(message: string = "Creating Assessment...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinner != null && this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  editSeries(
    calendarEvent: CalendarEvent,
    patientChart: Patient,
    teams: CompanyTeam[],
    roles: CompanyRole[]
  ) {
    this.presentEditSeriesModal(calendarEvent, patientChart, teams, roles);
  }

  presentEditSeriesModal(
    calendarEvent: CalendarEvent,
    patientChart: Patient,
    teams: CompanyTeam[],
    companyRoles: CompanyRole[]
  ) {
    this.increaseSpinnerCount("Loading unassigned order ...");
    let params: any = {};
    params.dataType = "UNSCHEDULEDEVENT";
    params.mustFields = [{ name: "orderId", value: calendarEvent.orderId }];

    forkJoin(
      this.calendarService.getUnassignedVisits(params),
      this.careTeamsService.getPatientCaregiversTyped(patientChart.externalId)
    ).subscribe(
      ([unassignedOrderResponse, contacts]: [
        UnassignedOrder,
        PatientContact[]
      ]) => {
        let contact = contacts.filter((x: PatientContact) => {
          return x.primaryCaregiver === true;
        });
        let primary =
          contact && contact.length === 1 ? contact[0] : new PatientContact();
        this.decreaseSpinnerCount();

        if (unassignedOrderResponse["totalHits"] > 0) {
          let unassignedOrder = unassignedOrderResponse[
            "hits"
          ][0] as UnassignedOrder;
          let roles = this.calendarService.mapCalendarAsessmentRoles(
            unassignedOrder.orderDiscipline,
            companyRoles
          );

          let modal = this.modalCtrl.create(CalendarScheduleOrder, {
            unscheduledOrder: unassignedOrder,
            patient: patientChart,
            primaryContact: primary,
            teams: teams,
            roles: roles,
          });

          modal.present();

          modal.onDidDismiss((data) => {
            if (Object.keys(data).length > 0) this.eventUpdated.emit();
          });
        } else {
          this.calendarService.presentErrorToast(
            null,
            "Could not find unassigned visit."
          );
        }
      },
      (err) => {
        let msg = "Error loading up details for Task Edit";
        this.decreaseSpinnerCount();
        this.calendarService.presentErrorToast(err, msg);
      }
    );
  }

  editEventView(
    calendarEvent: CalendarEvent,
    patientChart: Patient,
    teams: CompanyTeam[],
    roles: CompanyRole[]
  ) {
    this.presentEditEventModal(calendarEvent, patientChart, teams, roles);
  }

  presentEditEventModal(
    calendarEvent: CalendarEvent,
    patientChart: Patient,
    teams: CompanyTeam[],
    roles: CompanyRole[]
  ) {
    let editModal = this.modalCtrl.create(
      "CalendarEventEdit",
      {
        calendarEvent: calendarEvent,
        patientChart: patientChart,
        companyTeams: teams,
        companyRoles: roles,
        selectedResource: null,
        patientObject: patientChart,
        calendarTenantID: patientChart.id,
      },
      { enableBackdropDismiss: false }
    );
    editModal.present();

    editModal.onDidDismiss((data) => {
      if (Object.keys(data).length > 0) this.eventUpdated.emit();
    });
  }

  public showVisitQaQueueComments(event, visitId: string) {
    let commentCategory = this.commentCategories.find(
      (c) => c.category == this.visitsComments[visitId].category
    );
    let popover = this.popover.create(
      "QaCommentsPopover",
      {
        comment: this.visitsComments[visitId],
        category: commentCategory,
      },
      { cssClass: "qa-comments-popover" }
    );

    const ev = {
      target: {
        getBoundingClientRect: () => ({
          top: event.clientY + 12,
          left: event.clientX - 450,
        }),
      },
    };

    popover.present({ ev: ev });
    event.stopPropagation();
  }

  checkIfTaskPastDue() {
    if (this.task.updatedDatetime != null) {
      const updatedDate = new Date(this.task.updatedDatetime);
      const today = new Date();
      const startOfToday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0
      );
      if (updatedDate < startOfToday) {
        this.isTaskPastDue = true;
      }
    }
  }

  goToPatientProfile(chartId) {
    this.patientChartsService.PATIENT_ID = null;
    this.patientChartsService.CARE_TYPE = null;
    this.navSvc.goToPatientProfile(chartId);
  }

  checkIfTaskIsHopeType() {
    if (!this.task.hopeFlags || !Array.isArray(this.task.hopeFlags))
      return false;
    return (
      this.task.status !== this.VISIT_COMPLETED &&
      this.task.hopeFlags.some((flag) =>
        [
          HopeFlag.ADMISSION,
          HopeFlag.HUV1,
          HopeFlag.HUV2,
          HopeFlag.ADMISSION_SFV,
          HopeFlag.HUV1_SFV,
          HopeFlag.HUV2_SFV,
        ].includes(flag as HopeFlag)
      )
    );
  }
  getHopeType() {
    if (!this.task.hopeFlags || !Array.isArray(this.task.hopeFlags)) return "";

    let message = [];
    if (this.task.hopeFlags.includes(HopeFlag.ADMISSION))
      message.push(this.INITIAL_ASSESSMENT);
    if (this.task.hopeFlags.includes(HopeFlag.HUV1)) message.push(this.HUV1);
    if (this.task.hopeFlags.includes(HopeFlag.HUV2)) message.push(this.HUV2);
    if (
      this.task.hopeFlags.includes(HopeFlag.HUV1_SFV) ||
      this.task.hopeFlags.includes(HopeFlag.HUV2_SFV)
    )
      message.push(this.SFV);

    return "" + message.join(", ") + " ";
  }
}



// WEBPACK FOOTER //
// ./src/pages/common/dashboard/task-details-row/task-details-row.ts